import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import { QueryKey } from '../../constants/index';
import {
  EventLifecycleStatus,
  EventTimelineEntry,
  EventUnionWithPermissions,
  isDividendTimelineEvent,
} from '../../types/events.types';
import { EventsResponse } from './use-events';

type EventsInfinite = InfiniteData<EventsResponse>;
type EventsSnapshot = [readonly unknown[], EventsInfinite | undefined][];

const GET_EVENTS_FILTER = { queryKey: [QueryKey.GET_EVENTS] } as const;

// Optimistic-update helpers for the GET_EVENTS infinite-query caches. Every cached variation
// (filters/pagination) is patched via a partial key match so the optimistic row shows regardless
// of the active filter; snapshot()/restore() give onError rollback.
export const useOptimisticEvents = () => {
  const queryClient = useQueryClient();

  const cancel = () => queryClient.cancelQueries(GET_EVENTS_FILTER);

  const snapshot = (): EventsSnapshot =>
    queryClient.getQueriesData<EventsInfinite>(GET_EVENTS_FILTER);

  const restore = (snap: EventsSnapshot) =>
    snap.forEach(([key, data]) => queryClient.setQueryData(key, data));

  const mapEvents = (fn: (events: EventTimelineEntry[]) => EventTimelineEntry[]): void => {
    queryClient.setQueriesData<EventsInfinite>(GET_EVENTS_FILTER, (data) =>
      data
        ? { ...data, pages: data.pages.map((page) => ({ ...page, events: fn(page.events) })) }
        : data,
    );
  };

  // Dividend payout rows are never optimistically mutated — the Events module only reads them.
  // Rows are matched on `eventId` (the real Event id every mutation caller passes), NOT `id` —
  // mapEventToUnion overrides `id` with the subtype id for most event types (grant, fundraising,
  // buyback, …), so an `id` match silently no-ops and the pending edit/delete tag never shows.
  const setStatus = (eventId: string, lifecycleStatus: EventLifecycleStatus): void =>
    mapEvents((events) =>
      events.map((event) =>
        !isDividendTimelineEvent(event) && event.eventId === eventId
          ? { ...event, lifecycleStatus }
          : event,
      ),
    );

  const swapId = (fromId: string, toId: string): void =>
    mapEvents((events) =>
      events.map((event) =>
        !isDividendTimelineEvent(event) && event.eventId === fromId
          ? { ...event, eventId: toId, id: toId }
          : event,
      ),
    );

  const removeEvent = (eventId: string): void =>
    mapEvents((events) =>
      events.filter((event) => isDividendTimelineEvent(event) || event.eventId !== eventId),
    );

  // Prepends a pending-create placeholder to the first page of every cached list.
  const insertPendingCreate = (event: EventUnionWithPermissions): void => {
    queryClient.setQueriesData<EventsInfinite>(GET_EVENTS_FILTER, (data) => {
      if (!data || data.pages.length === 0) return data;
      const [firstPage, ...restPages] = data.pages;
      return {
        ...data,
        pages: [{ ...firstPage, events: [event, ...firstPage.events] }, ...restPages],
      };
    });
  };

  return { cancel, insertPendingCreate, removeEvent, restore, setStatus, snapshot, swapId };
};
